import { toPrettyDateTime } from "common";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { BreadCrumbs } from "../components/BreadCrumbs";
import { useUser } from "../store";
import { usersState } from "../store/users";
import { notify } from "../util/notify";
import { useAtom } from 'jotai'
import { useConfirmation } from "./ConfirmationProvider";
import "./Users.scss";

export default function Users() {
  const { invokeApi, user: currentUser, group } = useUser();
  const confirm = useConfirmation();
  const [users, setUsers] = useAtom(usersState);

  const refreshUsers = () => {
    invokeApi({ endpoint: "/getUsers" }).then((users) => {
      setUsers(users);
    });
  };
  useEffect(refreshUsers, []);

  const deleteUser = async (user) => {
    const confirmed = await confirm({
      title: "Delete user?",
      message: `Permanently delete the ${user.username} account?`,
      confirmLabel: "Delete user",
    });
    if (!confirmed) return;
    invokeApi({
      endpoint: "/deleteUser",
      data: { username: user.username },
    }).then((users) => {
      notify.success("successfully deleted user");
      refreshUsers();
    });
  };

  const issuePasswordReset = (user) => {
    invokeApi({
      endpoint: "/issuePasswordReset",
      data: { username: user.username },
    }).then(() => notify.success("Issue Reset Email Sent!"));
  };

  return (
    <>
      <BreadCrumbs
        path={[group, "users"]}
        labels={["My Facilities"]}
      />

      <main className="content users-page">
        <header className="users-page-heading">
          <div>
            <span className="users-page-eyebrow">Administration</span>
            <h1 className="title">Users</h1>
            <p>Manage access, facilities, and report preferences.</p>
          </div>
          <div className="users-page-heading-actions">
            <span className="users-count">
              <strong>{users?.users?.length ?? 0}</strong>
              {users?.users?.length === 1 ? " user" : " users"}
            </span>
            <Link to="create" className="button users-create-button">
              <i className="fa-solid fa-user-plus" aria-hidden="true" />
              <span>Create user</span>
            </Link>
          </div>
        </header>

        <section className="users-table-card" aria-label="User directory">
          <div className="users-table-heading">
            <div>
              <h2>User directory</h2>
              <p>Accounts and access settings for this organization</p>
            </div>
          </div>
          <div className="users-table-scroll">
            <table className="table is-fullwidth users-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Daily reports</th>
                  <th>Facilities</th>
                  <th>Type</th>
                  <th>Created at</th>
                  <th>Updated at</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users?.users?.length ? users.users.map((user) => {
                  const {
                    createdAt,
                    email,
                    facilities,
                    name,
                    receiveDailyReports,
                    type,
                    updatedAt,
                    username,
                  } = user;
                  return (
                    <tr key={username}>
                      <td className="users-username">{username}</td>
                      <td>{name}</td>
                      <td>{email}</td>
                      <td>
                        <span className={`users-preference${receiveDailyReports ? " is-enabled" : ""}`}>
                          <i className={`fa-solid ${receiveDailyReports ? "fa-check" : "fa-minus"}`} aria-hidden="true" />
                          {receiveDailyReports ? "Enabled" : "Off"}
                        </span>
                      </td>
                      <td className="users-facilities">
                        {facilities?.length ? (
                          <div className="users-facility-list">
                            {facilities.map((facility) => (
                              <span className="users-facility" key={facility}>
                                {facility}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="users-facility-empty">None assigned</span>
                        )}
                      </td>
                      <td><span className="users-type">{type}</span></td>
                      <td className="users-date">{toPrettyDateTime(createdAt)}</td>
                      <td className="users-date">{toPrettyDateTime(updatedAt)}</td>
                      <td>
                        <div className="users-row-actions">
                          <Link
                            to={username}
                            className="users-action users-action-edit"
                            aria-label={`Edit ${username}`}
                            title="Edit user"
                          >
                            <i className="fa-solid fa-pen" aria-hidden="true" />
                          </Link>
                          {user.username !== currentUser.username && (
                            <>
                              <button
                                className="users-action users-action-reset"
                                type="button"
                                onClick={() => issuePasswordReset(user)}
                                aria-label={`Send password reset to ${username}`}
                                title="Send password reset"
                              >
                                <i className="fa-solid fa-key" aria-hidden="true" />
                              </button>
                              <button
                                className="users-action users-action-delete"
                                type="button"
                                onClick={() => deleteUser(user)}
                                aria-label={`Delete ${username}`}
                                title="Delete user"
                              >
                                <i className="fa-solid fa-trash" aria-hidden="true" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td className="users-empty" colSpan={9}>No users to display.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </>
  );
}
